import { API_URL } from './api';
import { getAdminToken, getUserToken } from '../context/AuthContext';

const authHeaders = (): HeadersInit => {
    const token = getAdminToken();
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

async function req<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: { ...authHeaders(), ...(options?.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
    return data as T;
}

export const adminService = {
    getStatistics: () => req<any>('/admin/statistics'),
    getActivities: () => req<any>('/admin/activities'),
    getUsers: () => req<any[]>('/admin/users'),
    getUser: (id: string) => req<any>(`/admin/users/${id}`),
    updateUser: (id: string, body: any) =>
        req<any>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    grantCredits: (id: string, amount: number) =>
        req<any>(`/admin/users/${id}/grant-credits`, {
            method: 'POST',
            body: JSON.stringify({ amount }),
        }),
    getPayments: (params?: { status?: string }) => {
        const q = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
        return req<{ payments: any[]; summary: any } | any[]>(`/admin/payments${q}`);
    },
    reconcilePayments: () =>
        req<{ ok: boolean; results: any[] }>('/admin/payments/reconcile', { method: 'POST' }),
    getTemplates: (documentType?: 'cv' | 'letter') => {
        const q = documentType ? `?documentType=${documentType}` : '';
        return req<any[]>(`/admin/templates${q}`);
    },
    updateTemplate: (id: string, body: any) =>
        req<any>(`/admin/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    toggleTemplate: (id: string) =>
        req<any>(`/admin/templates/${id}/toggle`, { method: 'PATCH' }),
    getAnnouncements: (product?: 'cv' | 'letter') => {
        const q = product ? `?product=${product}` : '';
        return req<any[]>(`/admin/announcements${q}`);
    },
    createAnnouncement: (body: any) =>
        req<any>('/admin/announcements', { method: 'POST', body: JSON.stringify(body) }),
    updateAnnouncement: (id: string, body: any) =>
        req<any>(`/admin/announcements/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteAnnouncement: (id: string) =>
        req<any>(`/admin/announcements/${id}`, { method: 'DELETE' }),
    sendEmail: (body: any) =>
        req<any>('/admin/email/send', { method: 'POST', body: JSON.stringify(body) }),
    emailLogs: (product?: 'cv' | 'letter') => {
        const q = product ? `?product=${product}` : '';
        return req<any[]>(`/admin/email/logs${q}`);
    },
    testEmail: (to?: string) =>
        req<any>('/admin/email/test', { method: 'POST', body: JSON.stringify({ to }) }),
    getSettings: () => req<any>('/admin/settings'),
    updateSettings: (body: any) =>
        req<any>('/admin/settings', { method: 'PUT', body: JSON.stringify(body) }),
    getSupportTickets: () => req<any[]>('/support/admin/all'),
    getSupportTicket: (id: string) => req<any>(`/support/admin/${id}`),
    replySupport: (id: string, body: string) =>
        req<any>(`/support/admin/${id}/reply`, {
            method: 'POST',
            body: JSON.stringify({ body }),
        }),
    closeSupport: (id: string) =>
        req<any>(`/support/admin/${id}/close`, { method: 'PATCH' }),
    getNewsletterSubscribers: (status?: 'active' | 'unsubscribed') => {
        const q = status ? `?status=${encodeURIComponent(status)}` : '';
        return req<any[]>(`/newsletter/admin/all${q}`);
    },
    updateNewsletterSubscriber: (id: string, status: 'active' | 'unsubscribed') =>
        req<any>(`/newsletter/admin/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        }),
    deleteNewsletterSubscriber: (id: string) =>
        req<{ ok: boolean }>(`/newsletter/admin/${id}`, { method: 'DELETE' }),
};

export const publicTemplates = (documentType: 'cv' | 'letter' = 'cv') =>
    fetch(`${API_URL}/templates?documentType=${documentType}`).then(async (res) => {
        if (!res.ok) throw new Error('templates');
        return res.json();
    });

export const publicAnnouncements = (
    audience: 'all' | 'users' | 'guests' = 'all',
    product: 'cv' | 'letter' | 'all' = 'all'
) =>
    fetch(
        `${API_URL}/announcements/active?audience=${audience}&product=${product}`
    ).then(async (res) => {
        if (!res.ok) throw new Error('announcements');
        return res.json();
    });

/** Crédits paiement — session utilisateur (pas admin) */
export const paymentCredits = {
    get: async () => {
        const res = await fetch(`${API_URL}/payment/credits`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getUserToken() || ''}`,
            },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
        return data as { freeDownloadsRemaining: number };
    },
    use: async (documentId: string, documentType: 'cv' | 'letter' = 'cv') => {
        const res = await fetch(`${API_URL}/payment/use-credit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getUserToken() || ''}`,
            },
            body: JSON.stringify(
                documentType === 'letter'
                    ? { letterId: documentId, documentType: 'letter' }
                    : { cvId: documentId, documentType: 'cv' }
            ),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
        return data;
    },
};
